import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Sun, Moon, X } from 'lucide-react';
import { useTheme } from '../Context/ThemeContext';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Ana Sayfa', path: '/' },
    { name: 'Araçlar', path: '/araclar' },
    { name: 'Aracını Sat', path: '/aracini-sat' },
    { name: 'Hizmetler', path: '/hizmetler' },
    { name: 'Hakkımızda', path: '/hakkimizda' },
    { name: 'İletişim', path: '/iletisim' },
  ];

  return (
    <div className={`navbar-wrapper ${isScrolled ? 'navbar-scrolled' : ''}`}>
      <motion.nav 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
        className={`navbar-capsule ${isScrolled ? 'capsule-scrolled' : ''}`}
        onMouseLeave={() => setHoveredLink(null)}
      >
        {/* Logo Section - Cutout Circle */}
        <Link to="/" className="navbar-logo-area">
          <img 
            src="/images/ErenisLogo.png" 
            alt="Erenis Otomotiv" 
            className={`navbar-logo-img ${isScrolled ? 'logo-img-scrolled' : ''}`} 
          />
        </Link>

        {/* Center Navigation Links */}
        <ul className="navbar-links">
          {navLinks.map((link) => (
            <li
              key={link.path}
              className="navbar-link-item"
              onMouseEnter={() => setHoveredLink(link.path)}
            >
              <Link
                to={link.path}
                className={`navbar-link ${location.pathname === link.path ? 'navbar-link-active' : ''}`}
              >
                {link.name}
              </Link>
              {hoveredLink === link.path && (
                <motion.div
                  layoutId="navbar-pill-bg"
                  className="navbar-link-pill"
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                />
              )}
            </li>
          ))}
        </ul>

        {/* Right Actions */}
        <div className="navbar-actions">
          {/* Theme Toggle */}
          <button 
            className="navbar-theme-toggle"
            onClick={toggleTheme}
            aria-label={isDark ? 'Light temaya geç' : 'Dark temaya geç'}
          >
            <AnimatePresence mode="wait">
              {isDark ? (
                <motion.div
                  key="sun"
                  initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                  animate={{ rotate: 0, opacity: 1, scale: 1 }}
                  exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
                  transition={{ duration: 0.3 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Sun size={18} strokeWidth={2} />
                </motion.div>
              ) : (
                <motion.div
                  key="moon"
                  initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
                  animate={{ rotate: 0, opacity: 1, scale: 1 }}
                  exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
                  transition={{ duration: 0.3 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Moon size={18} strokeWidth={2} />
                </motion.div>
              )}
            </AnimatePresence>
          </button>

          <Link to="/teklif-al" className="navbar-btn-solid">
            Teklif Al
          </Link>
          <button className="navbar-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="navbar-mobile-menu"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar-mobile-link ${location.pathname === link.path ? 'navbar-mobile-link-active' : ''}`}
                onClick={() => setMobileOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="navbar-mobile-actions">
              <button className="navbar-theme-toggle navbar-theme-toggle-mobile" onClick={toggleTheme}>
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
                <span>{isDark ? 'Light Tema' : 'Dark Tema'}</span>
              </button>
              <Link to="/teklif-al" className="navbar-btn-solid" onClick={() => setMobileOpen(false)}>
                Teklif Al
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Navbar;
